// ============================================
// Premier Case Tracker — App Router
// ============================================

import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import LoginPage from './components/pages/LoginPage';
import RegisterPage from './components/pages/RegisterPage';
import ArchitectDashboardPage from './components/pages/ArchitectDashboardPage';
import OperationsManagerDashboardPage from './components/pages/OperationsManagerDashboardPage';
import TeamLeaderDashboardPage from './components/pages/TeamLeaderDashboardPage';
import QueueManagerDashboardPage from './components/pages/QueueManagerDashboardPage';
import SMEDashboardPage from './components/pages/SMEDashboardPage';
import PremierEngineerDashboardPage from './components/pages/PremierEngineerDashboardPage';
import type { UserRole } from './types';

function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles: UserRole[] }) {
  const { user, loading, token } = useAuth();

  if (loading) {
    return (
      <div className="flex-center" style={{ minHeight: '100vh' }}>
        <div className="spinner spinner--lg" />
      </div>
    );
  }

  if (!token || !user) return <Navigate to="/login" replace />;
  if (!allowedRoles.includes(user.role)) return <Navigate to="/login" replace />;

  return <>{children}</>;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex-center" style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
        <div className="spinner spinner--lg" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={`/${user.role.replace(/_/g, '-')}-dashboard`} replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to={`/${user.role.replace(/_/g, '-')}-dashboard`} replace /> : <RegisterPage />} />

      <Route path="/architect-dashboard" element={
        <ProtectedRoute allowedRoles={['architect']}><ArchitectDashboardPage /></ProtectedRoute>
      } />
      <Route path="/operations-manager-dashboard" element={
        <ProtectedRoute allowedRoles={['operations_manager']}><OperationsManagerDashboardPage /></ProtectedRoute>
      } />
      <Route path="/team-leader-dashboard" element={
        <ProtectedRoute allowedRoles={['team_leader']}><TeamLeaderDashboardPage /></ProtectedRoute>
      } />
      <Route path="/queue-manager-dashboard" element={
        <ProtectedRoute allowedRoles={['queue_manager']}><QueueManagerDashboardPage /></ProtectedRoute>
      } />
      <Route path="/sme-dashboard" element={
        <ProtectedRoute allowedRoles={['sme']}><SMEDashboardPage /></ProtectedRoute>
      } />
      <Route path="/premier-engineer-dashboard" element={
        <ProtectedRoute allowedRoles={['premier_engineer']}><PremierEngineerDashboardPage /></ProtectedRoute>
      } />

      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
